import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { apiUrl } from './api-url';

type AuthAccessResponse = {
  hasAccess?: unknown;
};

@Injectable({
  providedIn: 'root',
})
export class AuthAccessService {
  private accessCheck$?: Observable<boolean>;
  private accessCheckAccountKey: string | null = null;
  private readonly endpoint = apiUrl('Auth/access');

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService,
  ) {}

  hasAccess(): Observable<boolean> {
    this.ensureActiveAccount();
    const currentAccountKey = this.getCurrentAccountKey();

    if (this.accessCheck$ && this.accessCheckAccountKey !== currentAccountKey) {
      this.reset();
    }

    if (!this.accessCheck$) {
      this.accessCheckAccountKey = currentAccountKey;

      this.accessCheck$ = this.http.get<AuthAccessResponse>(this.endpoint).pipe(
        map((response) => response?.hasAccess === true),
        catchError(() => {
          this.reset();
          return of(false);
        }),
        shareReplay(1),
      );
    }

    return this.accessCheck$;
  }

  reset(): void {
    this.accessCheck$ = undefined;
    this.accessCheckAccountKey = null;
  }

  private ensureActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  private getCurrentAccountKey(): string | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    return activeAccount?.homeAccountId ?? null;
  }
}
